import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {ApplicableTableSettings, mblStatus} from '../../../../tableSettings';

import IfmsDate from '../../../../ui/Date';
import TextBox from '../../../../ui/TextBox';
import CheckBox from '../../../../ui/CheckBox';
import {Time} from '../../../../ui/DateTime';
import SmartTable from '../../../../uiControls/SmartTable';
import OceanUnPortLookupField from '../../../../uiControls/lookup/OceanUnPortLookupField';
import {formatDate, formatDateTime, getTime, now} from '../../../../dateUtils';
import {PimsCntrEvent} from "../../../../fms.core/PimsCntrEvent";
import {DefaultContainerMilestoneStatus} from "./implementationConstants";
import { TableStorage } from '../../../../hooks/useTableStorage';
import { AiMbl } from '../../../../fms.core/AiMbl';
import { getOriginalValue } from '../../../../fms.core/data/TrackingModule';

function SelectRenderer({data, row}: any) {
  if (!row.original.selectable) {
    return <div className="ifms-table-default"><CheckBox value={false} disabled dark/></div>;
  }

  const toggleRow = data[0].toggleRow;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x: any) => x === row.index) >= 0;

  return <div className="ifms-table-default"><CheckBox value={row.original.selectable && selected} disabled={!row.original.selectable} onChange={() => toggleRow(row.index, row)} /></div>;
}

function StatusNameRenderer({ cell }:any) {
  return <div style={{textAlign: 'left', marginLeft: '5px', paddingTop: '2px', fontSize: '11px'}}>{cell.value}</div>;
}

function DateRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <div style={{marginTop: '0px'}}><IfmsDate disabled value={row.original.date}/></div>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;

  function onChange(date: string|null) {
    if (!date) {
      onRowChange(row.index, {
        date: null,
        time: null,
        datetime: null,
      });
    } else {
      const tIndex = date.indexOf('T');
      const datetime = tIndex >= 0 ? `${date.substring(0, tIndex)}T${row.original.time}` : row.original.datetime;

      onRowChange(row.index, {
        date: date,
        datetime,
      });
    }
  }

  return <div style={{marginTop: '0px'}}><IfmsDate disabled={!selected} value={row.original.date} onChange={onChange} /></div>;
}

function TimeRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <div style={{marginTop: '0px'}}><TextBox disabled dark placeholder={'HH:MM'}
                                                    value={row.original.time || ''}/></div>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;
  
  function onChange(changedTime: string|null) {
    if (!row.original.date) {
      return;
    }

    const timeToApply = changedTime ?? '00:00';

    const tIndex = row.original.date.indexOf('T');
    const datetime = tIndex >= 0 ? `${row.original.date.substring(0, tIndex)}T${timeToApply}` : row.original.datetime;

    onRowChange(row.index, {
      time: timeToApply,
      datetime,
    });
  }

  return <div style={{marginTop: '0px'}}><Time disabled={!selected} value={row.original.time} onChange={onChange} /></div>;
}

function LocationRenderer({data, row}:any) {
  if (!row.original.selectable) {
    return <span></span>;
  }

  const onRowChange = data[0].onRowChange;
  const selectedIndeces = data[0].selectedIndeces;

  const selected = selectedIndeces.findIndex((x:any) => x === row.index) >= 0;

  return selected
    ? <div style={{marginTop: '0px', width: '100%'}}>
      <OceanUnPortLookupField
        initialValue={row.original.locName ?? ''}
        onTextChange={locName => onRowChange(row.index, {locName})}
        onSelect={location => onRowChange(row.index, {locName: location.portName})}
        searchBy="portName"
      />
    </div>
    : <div className="ifms-table-align-left">
      {row.original.locName ?? ''}
    </div>;
}

function LastEventTimeRenderer({row, data}:any) {
  return (
    <TextBox value={formatDateTime(row.original.lastDate)} disabled/>
  );
}

function MawbAirLineStatus({
  status,
  tableStorage,
  onChange,
  mawb,
}:{
  status: Array<PimsCntrEvent>|null,
  onChange: (status: Array<PimsCntrEvent>) => void,
  tableStorage: TableStorage,
  mawb:AiMbl
}) {
  const [selectedIndeces, setSelectedIndeces] = useState<Array<any>>([]);

  function toggleRow(rowIndex:any, row:any) {
    const index = selectedIndeces.findIndex(x => x === rowIndex);
    if (index >= 0) {
      const indeces = [...selectedIndeces];
       indeces.splice(index, 1);
       setSelectedIndeces(indeces);

      let findProp1 = '';
      let findProp2 = '';
      switch(rowIndex){
        case 0:
          findProp1 = 'pickupLoc';
          findProp2 = 'pickupDate';
          break;
        case 1:
          findProp1 = 'atdLoc';
          findProp2 = 'atd';
            break;
        case 2:
          findProp1 = 'customsRlsLoc';
          findProp2 = 'customsRls';
          break;
        case 3:
          findProp1 = 'ataLoc';
          findProp2 = 'ata';
          break;
        case 4:
          findProp1 = 'availLoc';
          findProp2 = 'avail';
          break;
        case 5:
          findProp1 = 'delvLoc';
          findProp2 = 'delivery';
          break;
      }

      onRowChange(rowIndex, {
        check: false,
         date: null,
         time: null,
         datetime: getOriginalValue(mawb, findProp2),
        locName: getOriginalValue(mawb, findProp1),
      });
    } else {
      setSelectedIndeces([...selectedIndeces, rowIndex]);

      const currentTime = now();
      let tempLocName = '';
      const time = getTime(currentTime);

        switch (rowIndex) {
          case 0: case 1:
            tempLocName = mawb.departureName;
            break;
          case 2: case 3: case 4: case 5:
            tempLocName = mawb.discharge;
            break;
          default:
            tempLocName = '';
            break;
        }

      onRowChange(rowIndex, {
        check: true,
        date: formatDate(currentTime),
        time,
        datetime: currentTime,
        locName: tempLocName
      });
    }
  }
  
  function onRowChange(rowIndex:number, change:any) {
    if (!status) {
      return;
    }

    Object.assign(status[rowIndex], change);
    onChange([status[rowIndex]]);
  }

  const statusData = status ?? DefaultContainerMilestoneStatus;

  const tableOptions:ApplicableTableSettings = {
    tableName: 'mawblStatus',
    ...mblStatus,
    data: statusData.map((x:any, i:number) => ({...x, i, onRowChange, toggleRow, selectable: true, selectedIndeces, /* name: x.statusName */})),
    rowRenderers: [
      { accessor: 'check', Cell: SelectRenderer },
      { accessor: 'name', Cell: StatusNameRenderer },
      { accessor: 'date', Cell: DateRenderer },
      { accessor: 'time', Cell: TimeRenderer },
      { accessor: 'locName', Cell: LocationRenderer },
      { accessor: 'lastDate', Cell: LastEventTimeRenderer }
    ],
    customStorage: tableStorage,
  };

  return (
    <div>
      <SmartTable {...tableOptions} />
    </div>
  );
}

MawbAirLineStatus.propTypes = {
  //selectedIndex: PropTypes.number,
  status: PropTypes.array,
  //applyToAll: PropTypes.func,
  onChange: PropTypes.func,
  tableStorage: PropTypes.object,
  defaultStatus: PropTypes.any,
};

export default React.memo(MawbAirLineStatus);
